/**
 * React Query hooks + tiny date helpers for the Calendar screen.
 */

import { useQuery } from "@tanstack/react-query";

import {
  calendarApi,
  type AgendaResponse,
  type CalendarScope,
} from "../api/calendar";
import { useApi, useAuth } from "../auth/AuthProvider";

export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** Resolve the app's `LangId` to a BCP-47 locale tag for `Intl` APIs. */
export function intlLocale(lang: string): string {
  return lang === "uk" ? "uk-UA" : "en-US";
}

export function fmtMonth(d: Date, lang: string): string {
  return d.toLocaleDateString(intlLocale(lang), {
    month: "long",
    year: "numeric",
  });
}

export function fmtDay(d: Date, lang: string): string {
  return d.toLocaleDateString(intlLocale(lang), {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/** Build the 6×7 grid of days that make up a month view. */
export function buildMonthGrid(firstOfMonth: Date): Date[] {
  const start = new Date(firstOfMonth);
  // Mon = 0; Sun = 6
  const dow = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dow);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function useMonthAgenda(cursor: Date, scope?: CalendarScope) {
  const client = useApi();
  const { isAuthenticated } = useAuth();
  const grid = buildMonthGrid(cursor);
  const from = grid[0]!;
  const to = grid[grid.length - 1]!;
  // Same local-midnight UTC trick as ``useDayAgenda`` so the wire
  // range maps to whole user-local days regardless of tz offset.
  const fromIso = new Date(`${ymd(from)}T00:00`).toISOString();
  const toIso = new Date(
    `${ymd(addDays(to, 1))}T00:00`,
  ).toISOString();
  return useQuery<AgendaResponse>({
    enabled: isAuthenticated,
    queryKey: ["calendar", "month-agenda", fromIso, toIso, scope ?? "all"],
    queryFn: () =>
      calendarApi(client).agenda({ from: fromIso, to: toIso, scope }),
  });
}

/** Add ``n`` days to ``d`` and return a new ``Date`` (no mutation). */
export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

/**
 * Convert a ``YYYY-MM-DD`` calendar date into the UTC ISO string that
 * represents *midnight in the user's local time zone* on that date.
 *
 * The agenda endpoint accepts datetimes and runs date math against
 * the user's stored tz. Sending bare ``YYYY-MM-DD`` would parse as
 * midnight UTC and — for tz offsets like Europe/Kyiv (UTC+3) — leak
 * the queried range into the next local day, surfacing duplicates.
 * Sending the proper local-midnight UTC ISO keeps "May 10 in Kyiv"
 * exactly one local day.
 */
function localMidnightUtcIso(date: string): string {
  return new Date(`${date}T00:00`).toISOString();
}

export function useDayAgenda(date: string, scope?: CalendarScope) {
  const client = useApi();
  const { isAuthenticated } = useAuth();
  // Backend short-circuits ``range_end <= range_start`` to an empty
  // agenda, so we send ``[date 00:00 local, date+1 00:00 local)`` —
  // the full local day, no leakage into the next.
  const fromIso = localMidnightUtcIso(date);
  const toIso = localMidnightUtcIso(ymd(addDays(new Date(date + "T00:00"), 1)));
  return useQuery<AgendaResponse>({
    enabled: isAuthenticated,
    queryKey: ["calendar", "agenda", fromIso, toIso, scope ?? "all"],
    queryFn: () =>
      calendarApi(client).agenda({ from: fromIso, to: toIso, scope }),
  });
}
